import { Col, Row } from "../components/layout.jsx";
import {
  Form,
  FormField,
  FormInput,
  FormInputAppend,
  Yup,
  useForm,
  useFormAction,
  yupResolver,
} from "../components/forms.jsx";
import {
  Headline,
  ImageHeader,
  MainContent,
  PanelInfos,
} from "../templates/common/Components.jsx";
import { color, font, styled, theme } from "../util/style.js";
import { useCallback, useMemo, useRef, useState } from "react";
import {
  useMyProshopVouchersOrder,
  useMyProshopVouchersUsable,
} from "../services/myProshopVouchers.js";

import BudgetHeader from "../components/BudgetHeader.jsx";
import { Button } from "../components/typography.jsx";
import Container from "../templates/common/Container.jsx";
import LoggedInTemplate from "../templates/LoggedInTemplate.jsx";
import MyProshopVoucherItem from "../components/MyProshopVoucherItem.jsx";
import _pick from "lodash/pick";
import { formatCurrency } from "../../common/currency.js";
import header from "../img/services_bandeau.jpg";
import header2x from "../img/services_bandeau@2x.jpg";
import headerMobile from "../img/services_bandeau_mobile.jpg";
import headerMobile2x from "../img/services_bandeau_mobile@2x.jpg";
import { paths } from "../services/router.js";
import { useSelfMetaData } from "../services/self.js";

const ScrollRef = styled.div`
  position: relative;
  top: -100px;
`;

const Panel = styled.div`
  background: white;
  border-radius: ${theme("border-radius")};
  padding: ${theme("padding-container")};
  margin-bottom: 36px;
  hr {
    margin: 32px 0;
  }
`;

const Message = styled.p`
  ${font("small")}
  display: block;
  margin: 0;
  padding: 0;
  margin-top: 4px;
`;

const ErrorMessage = styled(Message)`
  color: ${color("input-error")};
  font-weight: bold;
`;

const InfoMessage = styled(Message)`
  color: ${color("primary")};
  font-weight: bold;
`;

const RowFormField = styled(function ({ ...props }) {
  return <FormField {...props} />;
})`
  .FormField-field {
    align-items: center;
    display: flex;
    justify-content: space-between;
  }
  .FormField-message {
    margin-top: -1em;
    padding-right: 140px;
    p {
      font-size: 1.2rem;
    }
  }
`;

const HeaderFormField = styled(RowFormField)`
  color: ${color("primary")};
  font-weight: bold;
`;

const PanelTitle = styled.div`
  ${font("headline5")}
  text-align: center;
`;

const PanelSubtitle = styled.div`
  ${font("paragraphReduced")}
  margin-top: 10px;
  text-align: center;
`;

const PanelAlert = styled.div`
  ${font("paragraphReduced")}
  color: ${color("primary")};
  font-weight: bold;
  margin-top: 10px;
  text-align: center;
`;

const VoucherAmountBetter = styled.strong`
  color: ${color("primary")};
`;
const VoucherRatio = styled.span`
  background: ${color("primary")};
  border-radius: 1em;
  color: white;
  display: inline-block;
  font-size: 1.2rem;
  line-height: 1em;
  padding: 0.2em 0.4em;
`;

const VoucherAmountLabel = styled(function ({ amount, ratio, ...props }) {
  if (ratio === 1) return `${formatCurrency(amount)} HT`;
  return (
    <span {...props}>
      {`${formatCurrency(amount)} HT `}
      <VoucherAmountBetter>
        {`→ ${formatCurrency(amount * ratio)} HT`}{" "}
        <VoucherRatio>+{Math.round(100 * (ratio - 1))}%</VoucherRatio>
      </VoucherAmountBetter>
    </span>
  );
})``;

const VouchersList = styled.div`
  ${MyProshopVoucherItem} + ${MyProshopVoucherItem} {
    margin-top: 30px;
  }
`;

const Buttons = styled.div`
  padding: 0 ${theme("padding-container")};
  display: flex;
  justify-content: space-between;
`;

function useMyProshopVoucherForm() {
  return useForm({
    mode: "onChange",
    defaultValues: {},
    resolver: yupResolver(Yup.object()),
  });
}

export default function MyProshopVoucherCreate() {
  const { company } = useSelfMetaData();

  const budgetGifts = company?.budgets?.gifts;

  const myProshopVoucherOrderAction = useMyProshopVouchersOrder();
  const [usableVouchers] = useMyProshopVouchersUsable();

  const topScroll = useRef();
  const scrollToFormTop = useCallback(() => {
    if (!topScroll) return;
    topScroll.current.scrollIntoView();
  }, [topScroll]);

  const [success, setSuccess] = useState(false);
  const [vouchers, setVouchers] = useState(null);

  const form = useMyProshopVoucherForm(budgetGifts?.totalBalance?.amount || 0);
  const {
    reset,
    register,
    watch,
    formState: { errors },
  } = form;

  const [onRequest, { loading: loadingMyProshopVoucher }] = useFormAction(
    form,
    myProshopVoucherOrderAction,
    {
      transformData: (data) => {
        const amounts = [];
        for (let amountPropName of Object.keys(data)) {
          const amount = parseInt(
            amountPropName.replace("amount-", "") || "0",
            10,
          );
          const quantity = parseInt(data[amountPropName] || "0", 10);
          for (let i = 0; i < quantity; i++) {
            amounts.push(amount);
          }
        }
        return amounts;
      },
      onError: (err, setError) => {
        setError(
          err?.path || "_global",
          err || {
            message:
              "Vos bons My Proshop n'ont pas pu être créés. Veuillez réessayer plus tard.",
          },
        );
      },
      onSuccess: (res) => {
        setSuccess(true);
        setVouchers(res);
        reset({});
        scrollToFormTop();
      },
    },
  );

  const values = watch();
  const sum = Object.keys(values || {}).reduce(
    (sum, propName) =>
      sum +
      parseInt(propName.replace("amount-", "") || "0", 10) *
        parseInt(values[propName] || "0", 10),
    0,
  );
  const isValidOrder =
    !!budgetGifts && sum > 0 && budgetGifts.totalBalance.amount - sum >= 0;

  const amountsAndMaximum = useMemo(
    function () {
      const amountsAndQuantities = (usableVouchers || []).reduce(
        (all, { amount, quantity }) => (
          (all[amount] = (all[amount] || 0) + quantity), all
        ),
        {},
      );
      const amounts = Object.keys(amountsAndQuantities).sort((a, b) => a - b);
      return amounts.map((amount) => {
        const max = Math.max(
          0,
          Math.min(
            amountsAndQuantities[amount],
            Math.floor(budgetGifts.totalBalance.amount / amount),
          ),
        );
        return { amount, max };
      });
    },
    [usableVouchers, budgetGifts],
  );

  const voucherRatio = 1.25;

  return (
    <LoggedInTemplate title="Bons My Proshop">
      <ImageHeader
        image={header}
        image2x={header2x}
        imageMobile={headerMobile}
        imageMobile2x={headerMobile2x}
        icon="tools-line"
        label="Bons My Proshop"
      />
      <MainContent>
        <Headline title="Créer un bon My Proshop" />
        <Container>
          <BudgetHeader
            budget={budgetGifts}
            budgetButton={null}
            titleLabel="Votre budget cadeaux"
          />
          <Row>
            <Col kind="aside">
              <PanelInfos
                label={`Commandez vos bons My Proshop directement sur ELITE`}
              >
                <p style={{ textAlign: "center" }}>
                  <Button to={paths.myProshopVouchers} ghost>
                    Retour à la liste des bons
                  </Button>
                </p>
              </PanelInfos>
            </Col>
            <Col kind="main">
              <ScrollRef ref={topScroll} />

              <Form
                onSubmit={onRequest}
                loading={loadingMyProshopVoucher}
                errors={errors}
              >
                <Panel>
                  {success ? (
                    <div>
                      <p>
                        <strong>Vos bons My Proshop ont été créés</strong>.
                        <br />
                        Ajoutez-les à votre commande sur My Proshop afin de une
                        déduction du montant du bon.
                      </p>
                      {Boolean(vouchers) && (
                        <VouchersList>
                          {vouchers.map((voucher) => (
                            <MyProshopVoucherItem
                              {...voucher}
                              shopAmount={
                                voucher.amount *
                                (+new Date(voucher.expirationDate) >
                                +new Date(2026, 1, 1, 0, 0, 0) // Seulement pour les bons après 2026
                                  ? voucherRatio
                                  : 1)
                              }
                              key={voucher.id}
                            />
                          ))}
                        </VouchersList>
                      )}
                    </div>
                  ) : (
                    <>
                      <PanelTitle>Vos bons My Proshop</PanelTitle>
                      <PanelSubtitle>
                        Pour rappel, les montants de vos bons My Proshop sont
                        déduits directement de votre budget cadeaux.
                        <br />
                        <br />
                        Sélectionnez le montant et la quantité souhaités pour
                        chacun de vos bons. Le montant total de vos bons ne peut
                        être supérieur au solde de budget cadeaux restant (
                        {formatCurrency(budgetGifts?.totalBalance?.amount || 0)}
                        ).
                      </PanelSubtitle>
                      <PanelAlert>
                        NOUVEAU : Vous bénéficiez d'une bonification de 25% sur
                        la valeur des bons !
                      </PanelAlert>
                      <hr />

                      <HeaderFormField
                        label={`Montant des bons`}
                        name={`amount`}
                      >
                        Quantité
                      </HeaderFormField>
                      {amountsAndMaximum.map(({ amount, max }) => (
                        <RowFormField
                          key={`amount-${amount}`}
                          label={
                            <VoucherAmountLabel
                              amount={amount}
                              ratio={voucherRatio}
                            />
                          }
                          name={`amount-${amount}`}
                          info={`Pour ${formatCurrency(
                            amount,
                          )} de budget cadeaux, je reçois ${formatCurrency(
                            amount * voucherRatio,
                          )} de bon My Proshop`}
                        >
                          {({ name, ...childProps }) => (
                            <FormInput
                              type="number"
                              {...register(name)}
                              {...childProps}
                              min={0}
                              step={1}
                              size={5}
                              max={max}
                            />
                          )}
                        </RowFormField>
                      ))}

                      {!!sum && !!budgetGifts && (
                        <>
                          <hr />
                          <InfoMessage>
                            Le montant de votre commande est de :{" "}
                            {formatCurrency(sum)} HT.{" "}
                          </InfoMessage>
                          {voucherRatio !== 1 && (
                            <Message>
                              Vous bénéficierez d'un montant de{" "}
                              <strong>
                                {formatCurrency(sum * voucherRatio)}
                              </strong>{" "}
                              sur vos commandes My Proshop.
                            </Message>
                          )}
                          {budgetGifts.totalBalance.amount - sum >= 0 ? (
                            <Message>
                              Il restera{" "}
                              <strong>
                                {formatCurrency(
                                  budgetGifts.totalBalance.amount - sum,
                                )}
                              </strong>{" "}
                              de points cadeaux après commande.
                            </Message>
                          ) : (
                            <ErrorMessage>
                              Votre buget de{" "}
                              {formatCurrency(budgetGifts.totalBalance.amount)}{" "}
                              ne vous permet pas de commander pour ce montant.
                            </ErrorMessage>
                          )}
                        </>
                      )}
                    </>
                  )}
                </Panel>
                {Boolean(!success) && (
                  <Buttons>
                    <Button type="submit" disabled={!isValidOrder}>
                      Je commande mes bons
                    </Button>
                  </Buttons>
                )}
              </Form>
            </Col>
          </Row>
        </Container>
      </MainContent>
    </LoggedInTemplate>
  );
}
